import { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { toast } from '@/lib/toast';

export type InlineEditKind = 'text' | 'number' | 'currency';

interface InlineEditCellProps {
  /** Current value. */
  value: string | number | null;
  /** How the value is displayed and parsed. */
  kind: InlineEditKind;
  /**
   * Called with the parsed new value when the user commits an *actual* change
   * (string for `text`, number | null for `number` / `currency`). Not called
   * when the value is unchanged or invalid.
   */
  onCommit: (next: string | number | null) => void;
  /** Disables editing while a save is in flight. */
  pending?: boolean;
  /** Placeholder shown (italic) when the value is empty. */
  emptyLabel?: string;
  /** Minimum allowed value for numeric kinds. Default 0. */
  min?: number;
  className?: string;
}

/**
 * A click-to-edit table cell: shows a formatted value, turns into an input on
 * click, and commits on Enter or blur (Escape cancels). The one inline-editor
 * primitive — `NurseryPriceEditor`, `VariantFieldCell` and the inventory cell
 * are all thin wrappers around it, so every editable cell behaves identically.
 */
export function InlineEditCell({
  value,
  kind,
  onCommit,
  pending = false,
  emptyLabel = 'Set value',
  min = 0,
  className,
}: InlineEditCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const display =
    value == null || value === ''
      ? null
      : kind === 'currency'
        ? formatCurrency(Number(value))
        : String(value);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(value == null ? '' : String(value));
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();

    if (kind === 'text') {
      const next = trimmed === '' ? null : trimmed;
      if (next === (value ?? null)) return;
      onCommit(next);
      return;
    }

    // number / currency
    const next = trimmed === '' ? null : Number(trimmed);
    if (next != null && (isNaN(next) || next < min)) {
      toast.error('Enter a valid number');
      return;
    }
    if (next === (value ?? null)) return;
    onCommit(next);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={kind === 'text' ? 'text' : 'number'}
        step={kind === 'currency' ? '0.01' : '1'}
        min={kind === 'text' ? undefined : min}
        value={draft}
        autoFocus
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            setEditing(false);
          }
        }}
        className={cn(
          'h-9 w-full min-w-[88px] rounded border border-primary bg-background px-1.5 text-base text-foreground outline-none sm:h-7 sm:text-sm',
          kind !== 'text' && 'font-mono',
          className,
        )}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      disabled={pending}
      title="Click to edit"
      className={cn(
        'group flex h-9 w-full min-w-[88px] items-center gap-1 rounded border border-transparent px-1.5 text-left text-base transition-colors sm:h-7 sm:text-sm',
        'hover:border-primary hover:bg-primary/10 hover:text-foreground',
        kind !== 'text' && 'font-mono',
        display != null ? 'text-foreground' : 'italic text-muted-foreground',
        pending && 'opacity-50',
        className,
      )}
    >
      <span className="flex-1 truncate">{display ?? emptyLabel}</span>
      <Pencil
        aria-hidden
        className="h-3 w-3 shrink-0 text-primary opacity-0 transition-opacity group-hover:opacity-100"
      />
    </button>
  );
}
